import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BellRing,
  Check,
  Pencil,
  Plus,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Page, PageHeader, Empty } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const QUICK_STARTS = [
  "Fume Cupboard Testing",
  "PAT Testing",
  "Pressure Vessel Testing",
  "CLEAPSS subscription",
  "Ask Jonny subscription",
];

const blank = { name: "", due_date: "", notes: "" };

const displayDate = (value) =>
  new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const statusClass = (status) => {
  if (status === "Overdue")
    return "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300";
  if (status === "Due today" || status.includes("1 week"))
    return "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300";
  if (status.includes("4 weeks"))
    return "bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300";
  return "bg-muted text-muted-foreground";
};

function ReminderDialog({ reminder, onClose, onSaved }) {
  const [form, setForm] = useState({ ...blank, ...reminder });
  const [template, setTemplate] = useState("custom");
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const chooseTemplate = (value) => {
    setTemplate(value);
    if (value !== "custom") set("name")(value);
  };
  const save = async () => {
    setBusy(true);
    try {
      if (reminder?.id) {
        await api.put(`/annual-reminders/${reminder.id}`, form);
        toast.success("Annual reminder updated");
      } else {
        await api.post("/annual-reminders", form);
        toast.success("Annual reminder added");
      }
      onSaved();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="annual-reminder-dialog">
        <DialogHeader>
          <DialogTitle>
            {reminder?.id ? "Edit annual reminder" : "Add annual reminder"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!reminder?.id && (
            <div className="space-y-1">
              <Label>Start with</Label>
              <Select value={template} onValueChange={chooseTemplate}>
                <SelectTrigger data-testid="annual-reminder-template-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUICK_STARTS.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom annual reminder</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(event) => set("name")(event.target.value)}
              data-testid="annual-reminder-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Next due date</Label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(event) => set("due_date")(event.target.value)}
              data-testid="annual-reminder-due-date-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(event) => set("notes")(event.target.value)}
              data-testid="annual-reminder-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="annual-reminder-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy || !form.name.trim() || !form.due_date}
            data-testid="annual-reminder-save-button"
          >
            {busy ? "Saving…" : "Save reminder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReminderRow({ reminder, mine, onEdit, onComplete, onDelete }) {
  return (
    <div
      className="border-b last:border-b-0 px-4 py-4"
      data-testid={`annual-reminder-row-${reminder.id}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2
              className="font-semibold"
              data-testid={`annual-reminder-name-${reminder.id}`}
            >
              {reminder.name}
            </h2>
            <span
              className={
                `rounded-full px-2 py-0.5 text-[11px] font-medium ${statusClass(reminder.status)}`
              }
              data-testid={`annual-reminder-status-${reminder.id}`}
            >
              {reminder.status}
            </span>
          </div>
          <p
            className="mt-1 text-sm text-muted-foreground"
            data-testid={`annual-reminder-due-${reminder.id}`}
          >
            Due {displayDate(reminder.due_date)}
            {!mine && ` · ${reminder.technician_name}`}
          </p>
          {reminder.notes && (
            <p
              className="mt-2 whitespace-pre-wrap text-sm"
              data-testid={`annual-reminder-notes-${reminder.id}`}
            >
              {reminder.notes}
            </p>
          )}
        </div>
        {mine && (
          <div className="flex flex-wrap items-center gap-1.5">
            <Button
              size="sm"
              onClick={() => onComplete(reminder)}
              data-testid={`annual-reminder-complete-${reminder.id}`}
            >
              <Check className="mr-1.5 h-3.5 w-3.5" />
              Done this year
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onEdit(reminder)}
              title="Edit reminder"
              data-testid={`annual-reminder-edit-${reminder.id}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onDelete(reminder)}
              title="Delete reminder"
              data-testid={`annual-reminder-delete-${reminder.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AnnualRemindersPage() {
  const { user } = useAuth();
  const [scope, setScope] = useState("mine");
  const [reminders, setReminders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [completing, setCompleting] = useState(null);
  const requestId = useRef(0);
  const load = useCallback(() => {
    const currentRequest = ++requestId.current;
    api
      .get("/annual-reminders", { params: { scope } })
      .then((response) => {
        if (currentRequest === requestId.current) setReminders(response.data);
      })
      .catch(() => {});
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(
    () => ({
      overdue: reminders.filter((item) => item.status === "Overdue").length,
      soon: reminders.filter(
        (item) => item.status.includes("week") || item.status === "Due today",
      ).length,
    }),
    [reminders],
  );
  const complete = async () => {
    try {
      await api.post(`/annual-reminders/${completing.id}/complete`);
      toast.success(`${completing.name} moved to next year`);
      setCompleting(null);
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/annual-reminders/${deleting.id}`);
      toast.success("Annual reminder removed");
      setDeleting(null);
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const showAll = user.role === "admin";

  return (
    <Page wide>
      <PageHeader
        title="Annual reminders"
        subtitle="Annual checks and subscriptions."
      >
        <Button
          onClick={() => setEditing({})}
          data-testid="annual-reminder-add-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add reminder
        </Button>
      </PageHeader>
      <div
        className="mb-6 grid gap-3 sm:grid-cols-2"
        data-testid="annual-reminder-summary"
      >
        <div className="border-l-4 border-rose-500 bg-rose-50 px-4 py-3 dark:bg-rose-950/30">
          <div
            className="flex items-center gap-2 text-sm font-medium text-rose-900 dark:text-rose-200"
          >
            <TriangleAlert className="h-4 w-4" />
            Overdue
          </div>
          <p
            className="mt-1 text-2xl font-bold text-rose-900 dark:text-rose-100"
            data-testid="annual-reminder-overdue-count"
          >
            {summary.overdue}
          </p>
        </div>
        <div className="border-l-4 border-sky-500 bg-sky-50 px-4 py-3 dark:bg-sky-950/30">
          <div
            className="flex items-center gap-2 text-sm font-medium text-sky-900 dark:text-sky-200"
          >
            <BellRing className="h-4 w-4" />
            Due soon
          </div>
          <p
            className="mt-1 text-2xl font-bold text-sky-900 dark:text-sky-100"
            data-testid="annual-reminder-soon-count"
          >
            {summary.soon}
          </p>
        </div>
      </div>
      {showAll && (
        <Tabs value={scope} onValueChange={setScope} className="mb-4">
          <TabsList>
            <TabsTrigger value="mine" data-testid="annual-reminder-scope-mine">
              My reminders
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="annual-reminder-scope-all">
              Department view
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {reminders.length === 0 ? (
        <Empty>No annual reminders here yet.</Empty>
      ) : (
        <div
          className="rounded-xl border bg-card"
          data-testid="annual-reminder-list"
        >
          {reminders.map((reminder) => (
            <ReminderRow
              key={reminder.id}
              reminder={reminder}
              mine={reminder.technician_id === user.id}
              onEdit={setEditing}
              onComplete={setCompleting}
              onDelete={setDeleting}
            />
          ))}
        </div>
      )}
      {editing && (
        <ReminderDialog
          reminder={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      <AlertDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialogContent data-testid="annual-reminder-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This annual reminder will no longer notify you.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="annual-reminder-delete-cancel">
              Keep
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              data-testid="annual-reminder-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={!!completing}
        onOpenChange={(open) => !open && setCompleting(null)}
      >
        <AlertDialogContent data-testid="annual-reminder-complete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Mark “{completing?.name}” done this year?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The next due date will move forward by one year.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="annual-reminder-complete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={complete}
              data-testid="annual-reminder-complete-confirm"
            >
              Mark done
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
